import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { AlertCircle, CheckCircle2, Play, Rocket } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/shared/empty-state";
import { toastMutationError } from "@/lib/utils/formatting";
import {
  getGetApiConnectorPackagesQueryKey,
  useGetApiConnectorPackagesConnectorPackageIdGeneratedArtifacts,
  usePostApiConnectorPackagesConnectorPackageIdToolTestsRun,
  usePostApiConnectorPackagesConnectorPackageIdPublish,
} from "@/lib/api/generated/connector-packages/connector-packages";
import type {
  ConnectorPackageDiagnosticDto,
  ConnectorPackageDto,
  ConnectorPackageTestRunDto,
} from "@/lib/api/generated/control-api.schemas";

type ValidationState = {
  packageId: string;
  diagnostics: ConnectorPackageDiagnosticDto[];
  ok: boolean;
} | null;

export function ValidationPanel({ state }: { state: ValidationState }) {
  if (!state) {
    return <EmptyState title="No validation run" description="Run validation from the Source tab." />;
  }
  if (state.ok) {
    return (
      <Alert>
        <CheckCircle2 className="size-4" />
        <AlertTitle>Validation passed</AlertTitle>
        <AlertDescription>The static source template and import boundaries are valid.</AlertDescription>
      </Alert>
    );
  }
  return (
    <div className="grid gap-2">
      {state.diagnostics.map((diagnostic, index) => (
        <Alert key={`${diagnostic.code}-${diagnostic.relativePath ?? "package"}-${index}`} variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>{diagnostic.code}</AlertTitle>
          <AlertDescription>
            {diagnostic.relativePath ? `${diagnostic.relativePath}: ` : ""}
            {diagnostic.message}
          </AlertDescription>
        </Alert>
      ))}
    </div>
  );
}

export function GeneratedArtifactsPanel({ selectedPackage }: { selectedPackage?: ConnectorPackageDto }) {
  const packageId = selectedPackage?.connectorPackageId ?? "";
  const artifacts = useGetApiConnectorPackagesConnectorPackageIdGeneratedArtifacts(packageId, {
    query: { enabled: Boolean(packageId) },
  });

  if (!selectedPackage) {
    return <EmptyState title="No package selected" description="Select a connector package first." />;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <JsonCard title="Descriptor" value={artifacts.data?.descriptor} loading={artifacts.isLoading} />
      <JsonCard title="Manifest" value={artifacts.data?.manifest} loading={artifacts.isLoading} />
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>Source Hash</CardTitle>
        </CardHeader>
        <CardContent>
          <code className="block rounded-md bg-muted p-3 text-xs">{artifacts.data?.sourceHash ?? "Not generated yet"}</code>
        </CardContent>
      </Card>
    </div>
  );
}


export function PublishPanel({ selectedPackage }: { selectedPackage?: ConnectorPackageDto }) {
  const queryClient = useQueryClient();
  const publish = usePostApiConnectorPackagesConnectorPackageIdPublish();
  const runToolTest = usePostApiConnectorPackagesConnectorPackageIdToolTestsRun();
  const [latestRun, setLatestRun] = useState<ConnectorPackageTestRunDto | null>(null);
  const packageId = selectedPackage?.connectorPackageId ?? "";
  const artifacts = useGetApiConnectorPackagesConnectorPackageIdGeneratedArtifacts(packageId, {
    query: { enabled: Boolean(packageId) },
  });

  if (!selectedPackage) {
    return <EmptyState title="No package selected" description="Select a connector package first." />;
  }

  const latestVersion = selectedPackage.versions.toSorted((left, right) => right.version - left.version)[0];
  const compiledTools = readCompiledTools(artifacts.data?.manifest);
  const firstTool = compiledTools[0];
  const latestRunPassed = latestRun?.status === "passed";
  const isBusy = publish.isPending || runToolTest.isPending || artifacts.isLoading;

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_420px]">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Rocket className="size-4" />
            Publish Package
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">Draft v{selectedPackage.draftVersion}</Badge>
            <Badge variant={selectedPackage.publishedVersion ? "default" : "outline"}>
              Published {selectedPackage.publishedVersion ? `v${selectedPackage.publishedVersion}` : "none"}
            </Badge>
            <Badge variant="outline">{selectedPackage.status}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Publish validates the draft and stores immutable generated descriptor and manifest artifacts in the API.
          </p>
          <div className="grid gap-2 rounded-md border border-border p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Required Test</p>
                <p className="text-xs text-muted-foreground">
                  {firstTool
                    ? `Runs ${firstTool.callableName} against the current compiled source hash.`
                    : "No compiled connector tool is available for this draft."}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (!firstTool) return;
                  runToolTest.mutate(
                    {
                      connectorPackageId: selectedPackage.connectorPackageId,
                      data: {
                        toolName: firstTool.callableName,
                        input: {},
                      },
                    },
                    {
                      onSuccess: (result) => {
                        setLatestRun(result);
                        toast[result.status === "passed" ? "success" : "error"](
                          result.status === "passed" ? "Connector test passed" : "Connector test failed",
                        );
                      },
                      onError: (error) => toast.error(toastMutationError(error, "Failed to run connector test")),
                    },
                  );
                }}
                disabled={!firstTool || isBusy}
              >
                <Play className="size-4" />
                {runToolTest.isPending ? "Running..." : "Run Test"}
              </Button>
            </div>
            {latestRun ? (
              <Alert variant={latestRunPassed ? "default" : "destructive"}>
                {latestRunPassed ? <CheckCircle2 className="size-4" /> : <AlertCircle className="size-4" />}
                <AlertTitle>{latestRunPassed ? "Test passed" : "Test failed"}</AlertTitle>
                <AlertDescription>
                  {latestRun.toolName} · {latestRun.sourceHash}
                </AlertDescription>
              </Alert>
            ) : null}
          </div>
          <Button
            type="button"
            onClick={() =>
              publish.mutate(
                { connectorPackageId: selectedPackage.connectorPackageId },
                {
                  onSuccess: () => {
                    toast.success("Connector package published");
                    void queryClient.invalidateQueries({ queryKey: getGetApiConnectorPackagesQueryKey() });
                  },
                  onError: (error) => toast.error(toastMutationError(error, "Failed to publish package")),
                },
              )
            }
            disabled={publish.isPending || runToolTest.isPending}
          >
            <Rocket className="size-4" />
            Publish Draft
          </Button>
          <JsonCard title="Latest Published Manifest" value={latestVersion?.manifest} loading={publish.isPending} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Agent Activation</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <p className="text-sm text-muted-foreground">
            Published connectors are activated from an agent's Runtime Policy screen. One connector package can be enabled for many agents.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

type CompiledTool = {
  toolName: string;
  callableName: string;
};

function readCompiledTools(value: unknown): CompiledTool[] {
  if (!isRecord(value) || !Array.isArray(value.tools)) {
    return [];
  }
  return value.tools.flatMap((tool) => {
    if (!isRecord(tool)) {
      return [];
    }
    const toolName = typeof tool.toolName === "string" ? tool.toolName : "";
    const callableName = typeof tool.callableName === "string" ? tool.callableName : toolName;
    return toolName && callableName ? [{ toolName, callableName }] : [];
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function JsonCard({ title, value, loading }: { title: string; value: unknown; loading?: boolean }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <pre className="max-h-[420px] overflow-auto rounded-md bg-muted p-3 text-xs">
          {loading ? "Loading..." : value === undefined ? "Not available" : JSON.stringify(value, null, 2)}
        </pre>
      </CardContent>
    </Card>
  );
}
